/**
 * The fallback report (D247): one small, fire-and-forget beacon to
 * `POST {service}/api/storefront/fallback` whenever a handoff in the shopper's browser does not
 * end at our checkout, so the reason reaches our logs instead of only a `console.warn` in a
 * shopper's browser nobody will ever open. The name is historic: since D385 Shopify's checkout is
 * no fallback, so a report describes a shopper who was shown the retry notice, sent to the cart
 * page, or left to a store a person switched off (`./outcome.ts`). Four reasons go with a handoff
 * that goes on: `cart_token_mismatch` and `checkouts_token_mismatch` (the session cart replaced the
 * link's), `cart_update_failed` (`update.js` refused the form's fields, so `cart.js` was read) and
 * `cart_locale_failed` (the locale cart URL failed, so the root one was read).
 *
 * Diagnostics only. Nothing here changes where the shopper goes or when: the report is sent
 * synchronously before the navigation it describes, never awaited, and every failure is
 * swallowed (`sendFallbackReport`).
 *
 * Privacy: the payload carries the public key and shop domain (both already in the theme
 * source), an enum or reason code for everything else, and whether the page has a logged-in
 * customer as `logged_in` / `guest` / `unknown`. Never an email, a customer id or a URL query;
 * the checkout link only as its shape (`./selectors.ts` `checkoutLinkKind`). Since D352
 * (provisional, 1.7.5) it also carries the page's `location.pathname` (no query, no fragment),
 * the Shopify cart token **without** its `?key=` secret (`cartTokenFromCookie`) and `touch: true`
 * on a touch device — the same three members as the load report (D344, `./load-report.ts`) — so
 * the endpoint can keep each fallback as a `StorefrontFallbackReport` row an order paid in
 * Shopify's checkout is matched to by its `cart_token`. The cart token and the path are stored,
 * never logged; the log line is unchanged and the service adds no IP (`docs/STOREFRONT.md` §5).
 *
 * Shared with the endpoint (`src/app/api/storefront/fallback/handler.ts`), which validates every
 * member against these same lists, so the two can never drift apart. Pure and DOM-free so it is
 * unit-testable: `tests/unit/storefront/fallback-report.test.ts`.
 */

import { normaliseServiceUrl } from './config';
import type { PeakLoadReport } from './load-report';

/** Where the report goes, under the service origin. */
export const PEAK_FALLBACK_REPORT_PATH = '/api/storefront/fallback';

/**
 * What the shopper acted on: a link, a button (or any opt-in control), a form submit, a scripted
 * navigation, — `new_tab` — the handoff a page opened in a new tab or window runs on load
 * (D254: a checkout link opened with a middle click, ⌘/Ctrl/Shift or the context menu), or —
 * `form` — a cart-form post bound for checkout with no checkout control behind it (D255: a
 * theme's `requestSubmit()` or `form.submit()`, a hidden `checkout` field).
 */
export const PEAK_CONTROL_KINDS = [
  'anchor',
  'button',
  'submit',
  'navigation',
  'new_tab',
  'form',
  'none',
] as const;
export type PeakControlKind = (typeof PEAK_CONTROL_KINDS)[number];

/**
 * The shape of the checkout link, never the link: `./selectors.ts` `checkoutLinkKind`, or
 * `permalink` for a cart permalink held for the config and sent native (D260). `cart_checkout`
 * (`/cart/checkout`) since D372 (1.7.8); an older endpoint drops an unknown kind, never the report.
 */
export const PEAK_LINK_KINDS = [
  'checkout',
  'cart_checkout',
  'cart_c',
  'checkouts',
  'permalink',
  'none',
] as const;
export type PeakLinkKind = (typeof PEAK_LINK_KINDS)[number];

/**
 * Where the script was when it decided: still waiting for the config, switched on, switched off
 * by the service (`enabled: false`), or unable to read the config at all. `b2b` is what a bundle
 * before 1.8.0 sends for a B2B buyer (D385 took that stand-down out), so the endpoint keeps it.
 */
export const PEAK_CONFIG_STATES = ['loading', 'enabled', 'disabled', 'unavailable', 'b2b'] as const;
export type PeakConfigState = (typeof PEAK_CONFIG_STATES)[number];

/** From Liquid `customer` (`customerStateOf`); `unknown` when the tag does not say. */
export const PEAK_CUSTOMER_STATES = ['logged_in', 'guest', 'unknown'] as const;
export type PeakCustomerState = (typeof PEAK_CUSTOMER_STATES)[number];

/** Reason codes and config reasons: the same alphabet the `console.warn` lines already use. */
export const PEAK_REPORT_REASON_PATTERN = /^[a-z0-9_]{1,40}$/;

/** `window.peakCheckout.version`. */
export const PEAK_REPORT_VERSION_PATTERN = /^[0-9A-Za-z.-]{1,16}$/;

export type PeakFallbackReport = {
  publicKey: string;
  shopDomain: string;
  /** The `console.warn` reason code: `cart_unavailable`, `disabled`, `script_inactive`, … */
  reason: string;
  /** The bundle's own version. */
  version: string;
  control: PeakControlKind;
  link: PeakLinkKind;
  config: PeakConfigState;
  /** Why the config is `unavailable`: `config_unavailable`, `config_invalid`, `config_http_503`. */
  configReason?: string;
  /**
   * The HTTP status of `POST /api/checkout/create` when the server decided, or of the failed cart
   * read on `cart_empty_or_unreadable`, `cart_unavailable`, `cart_update_failed` and
   * `cart_locale_failed` (D385). Absent when nothing answered.
   */
  status?: number;
  customer: PeakCustomerState;
  /**
   * A tap an inline early-tap stub held before the script ran (D343; printed no more since D360,
   * still in the themes that pasted it): set by the stub's own `script_not_loaded` report, and by
   * the script for every report about a tap it took over from the stub. Absent otherwise — never
   * `false`.
   */
  early?: true;
  /** D352: `location.pathname` when the report was made — never the query or the fragment. */
  path?: string;
  /** D352: the Shopify cart token without its `?key=` part; absent without a readable cookie. */
  cartToken?: string;
  /** D352: `navigator.maxTouchPoints > 1` — tells an iPad on the desktop site from a Mac. */
  touch?: true;
};

/** D352: the page members of a fallback report, read when it is made (the cart may be new). */
export type PeakReportPage = {
  path?: string | undefined;
  cartToken?: string | undefined;
  touch?: true | undefined;
};

/**
 * The optional explicit marker: `data-peak-customer="true"` / `"false"`, which the advanced
 * snippet renders from Liquid `{% if customer %}`. Liquid's `customer` "returns `nil`" when the
 * customer isn't logged in (https://shopify.dev/docs/api/liquid/objects/customer).
 */
export const PEAK_CUSTOMER_ATTRIBUTE = 'data-peak-customer';

/**
 * Is a customer logged in on this page, as far as the tag can tell?
 *
 * 1. `data-peak-customer`, when it is exactly `true` or `false`.
 * 2. Otherwise `data-peak-b2b`, which the one-line install has carried since 2026-09-29 as
 *    `{{ customer.b2b? }}`: documented as "Returns `true` if the customer is a B2B customer.
 *    Returns `false` if not.", and empty for a guest, whose `customer` is `nil` (same page). So
 *    `true` / `false` mean logged in and an empty value means a guest. The advanced snippet
 *    renders that attribute only for a B2B buyer, so on a snippet install its absence says
 *    nothing — hence step 1.
 * 3. Neither: `unknown`. Every line pasted before 2026-09-29 lands here and keeps working.
 *
 * No undocumented global (`ShopifyAnalytics.meta`, `__st`) is read.
 */
export function customerStateOf(
  script: { getAttribute: (name: string) => string | null } | null,
): PeakCustomerState {
  if (script === null) return 'unknown';
  const explicit = (script.getAttribute(PEAK_CUSTOMER_ATTRIBUTE) || '').trim().toLowerCase();
  if (explicit === 'true') return 'logged_in';
  if (explicit === 'false') return 'guest';

  const b2b = script.getAttribute('data-peak-b2b');
  if (b2b === null) return 'unknown';
  const value = b2b.trim().toLowerCase();
  if (value === '') return 'guest';
  return value === 'true' || value === 'false' ? 'logged_in' : 'unknown';
}

/** The control kind for an event the interceptor (or the passive observer) saw. */
export function controlKindOf(
  eventType: string,
  isAnchor: boolean,
  hasControl: boolean,
): PeakControlKind {
  if (eventType === 'submit') return 'submit';
  if (eventType === 'navigate') return 'navigation';
  if (!hasControl) return 'none';
  return isAnchor ? 'anchor' : 'button';
}

/** `{service}/api/storefront/fallback`. */
export function fallbackEndpointUrl(serviceUrl: string): string {
  return normaliseServiceUrl(serviceUrl) + PEAK_FALLBACK_REPORT_PATH;
}

/**
 * What `sendFallbackReport` needs from the browser: `navigator` (so `sendBeacon` is called on its
 * own receiver) and a `fetch`. Either may be missing.
 */
export type PeakBeaconEnvironment = {
  navigator?: { sendBeacon?: (url: string, data: string) => boolean } | undefined;
  fetch?: ((input: string, init: RequestInit) => Promise<unknown>) | undefined;
};

/**
 * Send one report and forget it — a fallback report, or a load report (D344, `./load-report.ts`),
 * which shares the endpoint. Never throws, never returns a promise anyone waits on.
 *
 * `navigator.sendBeacon` first: it is queued by the browser and survives the navigation that
 * follows. The body is a string, sent as `text/plain;charset=UTF-8` — a CORS-safelisted type, so
 * there is no preflight (the endpoint parses JSON whatever the content type). When there is no
 * `sendBeacon`, or it refuses the payload (`false`), `fetch` with `keepalive: true`, which may
 * also outlive the page, `credentials: 'omit'` and `mode: 'cors'`; its result and any rejection
 * are ignored.
 */
export function sendFallbackReport(
  environment: PeakBeaconEnvironment,
  url: string,
  report: PeakFallbackReport | PeakLoadReport,
): void {
  try {
    const body = JSON.stringify(report);
    const nav = environment.navigator;
    if (nav && typeof nav.sendBeacon === 'function' && nav.sendBeacon(url, body)) return;
    if (typeof environment.fetch === 'function') {
      environment
        .fetch(url, { method: 'POST', mode: 'cors', credentials: 'omit', keepalive: true, body })
        .catch(() => undefined);
    }
  } catch {
    // A diagnostics beacon is never worth an exception on the merchant's page.
  }
}

/** The parts of a report that do not change during a page load. */
export type PeakReportBase = Pick<
  PeakFallbackReport,
  'publicKey' | 'shopDomain' | 'version' | 'customer'
>;

/**
 * The config state at the moment of reporting, and whether the report is about a tap the
 * installed early-tap stub held (D343): `early` becomes the report's `early: true`.
 */
export type PeakConfigSnapshot = {
  config: PeakConfigState;
  configReason?: string | undefined;
  early?: boolean | undefined;
};

export type PeakFallbackReporter = {
  /** One report, unconditionally: a fallback the interceptor itself decided. */
  report(reason: string, control: PeakControlKind, link: PeakLinkKind, status?: number): void;
  /** One report per `key` per page load; `false` when the key was already used. */
  reportOnce(key: string, reason: string, control: PeakControlKind, link: PeakLinkKind): boolean;
  /**
   * The passive observer: a checkout control was used while the script was **not** active.
   * Reports `script_inactive` once per page load, and never while the config is `enabled` —
   * then the active interceptor owns the event and reports its own fallback, so a click is
   * never counted twice.
   */
  observeInactive(control: PeakControlKind, link: PeakLinkKind): boolean;
};

/**
 * Ties the base, the live config state and the transport together. `page` (D352) is read at each
 * report for its path, cart token and touch flag; a member it leaves out stays out.
 */
export function createFallbackReporter(
  base: PeakReportBase,
  configState: () => PeakConfigSnapshot,
  send: (report: PeakFallbackReport) => void,
  page?: () => PeakReportPage,
): PeakFallbackReporter {
  const used: Record<string, true> = {};
  const report = (
    reason: string,
    control: PeakControlKind,
    link: PeakLinkKind,
    status?: number,
  ): void => {
    try {
      const state = configState();
      const payload: PeakFallbackReport = { ...base, reason, control, link, config: state.config };
      if (state.configReason) payload.configReason = state.configReason;
      if (status !== undefined) payload.status = status;
      if (state.early === true) payload.early = true;
      if (page) {
        const at = page();
        if (at.path) payload.path = at.path;
        if (at.cartToken) payload.cartToken = at.cartToken;
        if (at.touch) payload.touch = true;
      }
      send(payload);
    } catch {
      // Never let a report break the navigation it describes.
    }
  };
  const reportOnce = (
    key: string,
    reason: string,
    control: PeakControlKind,
    link: PeakLinkKind,
  ): boolean => {
    if (used[key]) return false;
    used[key] = true;
    report(reason, control, link);
    return true;
  };
  return {
    report,
    reportOnce,
    observeInactive: (control, link) =>
      configState().config !== 'enabled' &&
      reportOnce('inactive', 'script_inactive', control, link),
  };
}
